import { useState } from 'react';

import {
  IdeaAction,
  IdeaSummary,
  IdeaTier,
  ideaActionLabels,
  ideaErrorMessage,
} from '@/types/service/ideaExchange';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useActOnIdeaRequestMutation } from '@/store/api/ideaExchangeApi';

interface Props {
  request: IdeaSummary;
  tier: IdeaTier;
  action: IdeaAction['action'];
  onClose: () => void;
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  returnFocus: HTMLElement;
  fallbackFocus: React.RefObject<HTMLElement>;
}

const explanations: Record<IdeaAction['action'], string> = {
  plan: 'Planning locks further backing changes.',
  start: 'Mark this request as in progress.',
  ship: 'Each backing institution receives 1.5 times its points, rounded up. This status is final.',
  decline: 'Each backing institution receives a full points refund. This status is final.',
  update: 'Publish an update to the customer-facing thread.',
};

export function IdeaActionDialog({
  request,
  tier,
  action,
  onClose,
  onSaved,
  onBusyChange,
  returnFocus,
  fallbackFocus,
}: Props) {
  const [value, setValue] = useState(
    action === 'plan' || action === 'start' ? (request.estimate ?? '') : '',
  );
  const [message, setMessage] = useState('');
  const [submit, { isLoading }] = useActOnIdeaRequestMutation();
  const estimate = action === 'plan' || action === 'start';
  const needsText = action === 'plan' || action === 'decline' || action === 'update';

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (isLoading || (needsText && !value.trim())) return;
    let data: IdeaAction;
    switch (action) {
      case 'plan':
        data = { action, estimate: value.trim() };
        break;
      case 'start':
        data = value.trim() ? { action, estimate: value.trim() } : { action };
        break;
      case 'decline':
        data = { action, reason: value.trim() };
        break;
      case 'update':
        data = { action, body: value.trim() };
        break;
      case 'ship':
        data = { action };
        break;
    }
    onBusyChange(true);
    setMessage('');
    try {
      await submit({ id: request.id, tier, data }).unwrap();
      await onSaved();
      onClose();
    } catch (error) {
      setMessage(ideaErrorMessage(error, 'Could not save this action. Try again.'));
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
    >
      <DialogContent
        className='max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-1 overflow-y-auto'
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const target = returnFocus.isConnected ? returnFocus : fallbackFocus.current;
          target?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {ideaActionLabels[action]} request #{request.id}?
          </DialogTitle>
          <DialogDescription>{request.title}</DialogDescription>
        </DialogHeader>
        <p
          className={
            tier === 'production' ? 'font-semibold text-red-700 dark:text-red-400' : 'font-semibold'
          }
        >
          {tier === 'production' ? 'Production' : 'Nonprod'}
        </p>
        <p className='text-sm'>{explanations[action]}</p>
        <form onSubmit={save} className='space-y-4'>
          {action !== 'ship' && (
            <div className='space-y-2'>
              <Label htmlFor='idea-action-value'>
                {estimate
                  ? `Estimate${action === 'start' ? ' (optional)' : ''}`
                  : action === 'decline'
                    ? 'Reason'
                    : 'Update'}
              </Label>
              {estimate ? (
                <Input
                  id='idea-action-value'
                  name='estimate'
                  maxLength={20}
                  required={needsText}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  disabled={isLoading}
                  placeholder='Q1 2027'
                />
              ) : (
                <Textarea
                  id='idea-action-value'
                  name={action === 'decline' ? 'reason' : 'body'}
                  required
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  disabled={isLoading}
                  rows={5}
                />
              )}
            </div>
          )}
          {message && (
            <p role='alert' className='text-sm text-destructive'>
              {message}
            </p>
          )}
          <DialogFooter className='gap-2'>
            <Button type='button' variant='outline' disabled={isLoading} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant={action === 'decline' ? 'destructive' : 'default'}
              className={action === 'decline' ? undefined : 'text-black'}
              disabled={isLoading || (needsText && !value.trim())}
            >
              {isLoading ? 'Saving...' : `Confirm ${ideaActionLabels[action].toLowerCase()}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
